import React, {useState} from 'react';
import {Box, Typography, TextField, Button, Chip} from '@mui/material';
import {useGetProductionGuardView} from '../../../hooks/productions/useProductions';
import {GetProductionGuardViewRequestParam} from '../../../api/types/production.type';

const ProductionGuardViewApiTest: React.FC = () => {
  const [securityGroupId, setSecurityGroupId] = useState<number>(1);
  const [productionId, setProductionId] = useState<number>(59);
  const [enabled, setEnabled] = useState<boolean>(false);
  const [copySuccess, setCopySuccess] = useState<boolean>(false);

  // ✅ Hook will automatically fetch when enabled is true
  const {
    data: productionGuardData,
    isLoading,
    error,
    refetch,
  } = useGetProductionGuardView(
    {
      SecurityGroupId: securityGroupId,
      productionId: productionId,
    } as GetProductionGuardViewRequestParam,
    enabled // Only fetch when user clicks "Test API"
  );

  const handleTestApi = () => {
    setEnabled(true);
  };

  const handleReset = () => {
    setEnabled(false);
    // Note: React Query will keep the cached data, but we stop fetching
  };

  const handleCopyJson = async () => {
    if (productionGuardData) {
      try {
        await navigator.clipboard.writeText(
          JSON.stringify(productionGuardData, null, 2)
        );
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  return (
    <Box className="demo-page" padding={3}>
      <Typography
        variant="h4"
        className="typo-text headline2_size"
        gutterBottom
      >
        🔒 Production Guard View API Test
      </Typography>
      <Box
        sx={{
          backgroundColor: '#f5f5f5',
          padding: 2,
          borderRadius: 1,
          marginBottom: 3,
        }}
      >
        <Typography variant="h6" className="typo-text subhead2_size">
          Request Parameters
        </Typography>

        <Box sx={{display: 'flex', gap: 2, marginTop: 2, flexWrap: 'wrap'}}>
          <TextField
            label="Security Group ID"
            type="number"
            value={securityGroupId}
            onChange={(e) => setSecurityGroupId(Number(e.target.value))}
            size="small"
          />
          <TextField
            label="Production ID"
            type="number"
            value={productionId}
            onChange={(e) => setProductionId(Number(e.target.value))}
            size="small"
          />
        </Box>

        <Box sx={{marginTop: 2, display: 'flex', gap: 2}}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleTestApi}
            data-testid="production-guard-view-api-test-test-api-button">
            🚀 Test API
          </Button>
          <Button
            variant="outlined"
            onClick={handleReset}
            data-testid="production-guard-view-api-test-reset-button">
            🔄 Reset
          </Button>
          {enabled && (
            <Button
              variant="outlined"
              onClick={() => refetch()}
              data-testid="production-guard-view-api-test-refetch-button">
              🔄 Refetch
            </Button>
          )}
          {productionGuardData && (
            <Button
              variant="outlined"
              onClick={handleCopyJson}
              data-testid="production-guard-view-api-test-copy-json-button">
              📋 Copy JSON
            </Button>
          )}
          {copySuccess && <Chip color="success" label="Copied!" size="small" data-testid={`production-guard-view-chip-success`} />}
        </Box>
      </Box>
      {/* Loading State */}
      {isLoading && (
        <Box
          sx={{
            padding: 3,
            backgroundColor: '#e3f2fd',
            borderRadius: 1,
            border: '1px solid #2196f3',
          }}
        >
          <Typography className="typo-text body2_size">
            ⏳ Loading... Making API request...
          </Typography>
        </Box>
      )}
      {/* Error State */}
      {error && (
        <Box
          sx={{
            padding: 3,
            backgroundColor: '#ffebee',
            borderRadius: 1,
            border: '1px solid #f44336',
          }}
        >
          <Typography className="typo-text subhead3_size" color="error">
            ❌ Error
          </Typography>
          <Typography
            className="typo-text body3_size"
            color="error"
            sx={{marginTop: 1}}
          >
            {(error as Error).message}
          </Typography>
        </Box>
      )}
      {/* Success State - Display Data */}
      {productionGuardData && !isLoading && (
        <Box
          sx={{
            padding: 3,
            backgroundColor: '#e8f5e9',
            borderRadius: 1,
            border: '1px solid #4caf50',
            marginBottom: 2,
          }}
        >
          <Typography className="typo-text subhead2_size" gutterBottom>
            ✅ Response Data
          </Typography>

          {/* Key Information Display */}
          <Box sx={{marginTop: 2}}>
            <Typography className="typo-text label2_size">
              Key Information
            </Typography>
            <Box
              sx={{
                padding: 2,
                backgroundColor: '#fff',
                borderRadius: 1,
                marginTop: 1,
              }}
            >
              <Typography className="typo-text body3_size">
                <strong>Production ID:</strong>{' '}
                {productionGuardData.productionId}
              </Typography>
              <Typography className="typo-text body3_size">
                <strong>Security Group ID:</strong>{' '}
                {productionGuardData.securityGroupId}
              </Typography>
              <Typography className="typo-text body3_size">
                <strong>Total Documents Count:</strong>{' '}
                {productionGuardData.totalDocumentsCount}
              </Typography>
              <Typography className="typo-text body3_size">
                <strong>Family Documents Count:</strong>{' '}
                {productionGuardData.familyDocumentsCount}
              </Typography>
              <Typography className="typo-text body3_size">
                <strong>Production Status Code:</strong>{' '}
                {productionGuardData.productionStatusCode}
              </Typography>
              <Typography className="typo-text body3_size">
                <strong>Does Contain Privilege Docs:</strong>{' '}
                {productionGuardData.doesContainPrivilegeDocs ? 'Yes' : 'No'}
              </Typography>
              <Typography className="typo-text body3_size">
                <strong>Show Navigation Buttons:</strong>{' '}
                {productionGuardData.showNavigationButtons ? 'Yes' : 'No'}
              </Typography>
              <Typography className="typo-text body3_size">
                <strong>Is Mark Complete:</strong>{' '}
                {productionGuardData.isMarkComplete ? 'Yes' : 'No'}
              </Typography>
            </Box>
          </Box>

          {/* Metadata Details */}
          {productionGuardData.metaDataDetailsOnGuard &&
            productionGuardData.metaDataDetailsOnGuard.length > 0 && (
              <Box sx={{marginTop: 2}}>
                <Typography className="typo-text label2_size">
                  Metadata Details on Guard (
                  {productionGuardData.metaDataDetailsOnGuard.length} items)
                </Typography>
                <Box
                  sx={{
                    padding: 1,
                    backgroundColor: '#fff',
                    borderRadius: 1,
                    marginTop: 1,
                    maxHeight: 200,
                    overflow: 'auto',
                  }}
                >
                  {productionGuardData.metaDataDetailsOnGuard
                    .slice(0, 10)
                    .map((item, index) => (
                      <Chip
                        key={index}
                        label={`${item.metaDataValue} (ID: ${item.metaDataId})`}
                        size="small"
                        sx={{margin: 0.5}}
                        data-testid={`production-guard-view-chip-${item.metaDataValue}`} 
                      />
                    ))}
                  {productionGuardData.metaDataDetailsOnGuard.length > 10 && (
                    <Typography
                      className="typo-text body3_size"
                      sx={{marginTop: 1}}
                    >
                      ... and{' '}
                      {productionGuardData.metaDataDetailsOnGuard.length - 10}{' '}
                      more
                    </Typography>
                  )}
                </Box>
              </Box>
            )}

          {/* Production Fields */}
          {productionGuardData.productionFieldsOnGuard &&
            productionGuardData.productionFieldsOnGuard.length > 0 && (
              <Box sx={{marginTop: 2}}>
                <Typography className="typo-text label2_size">
                  Production Fields on Guard (
                  {productionGuardData.productionFieldsOnGuard.length} items)
                </Typography>
                <Box
                  sx={{
                    padding: 1,
                    backgroundColor: '#fff',
                    borderRadius: 1,
                    marginTop: 1,
                    maxHeight: 200,
                    overflow: 'auto',
                  }}
                >
                  {productionGuardData.productionFieldsOnGuard
                    .slice(0, 10)
                    .map((item, index) => (
                      <Chip
                        key={index}
                        label={`${item.metaDataValue} (ID: ${item.metaDataId})`}
                        size="small"
                        sx={{margin: 0.5}}
                        data-testid={`production-guard-view-chip-${item.metaDataValue}-${item.metaDataId}`}
                      />
                    ))}
                  {productionGuardData.productionFieldsOnGuard.length > 10 && (
                    <Typography
                      className="typo-text body3_size"
                      sx={{marginTop: 1}}
                    >
                      ... and{' '}
                      {productionGuardData.productionFieldsOnGuard.length - 10}{' '}
                      more
                    </Typography>
                  )}
                </Box>
              </Box>
            )}

          {/* Tag List */}
          {productionGuardData.tagList &&
            productionGuardData.tagList.length > 0 && (
              <Box sx={{marginTop: 2}}>
                <Typography className="typo-text label2_size">
                  Tag List ({productionGuardData.tagList.length} items)
                </Typography>
                <Box
                  sx={{
                    padding: 1,
                    backgroundColor: '#fff',
                    borderRadius: 1,
                    marginTop: 1,
                    maxHeight: 200,
                    overflow: 'auto',
                  }}
                >
                  {productionGuardData.tagList
                    .slice(0, 10)
                    .map((tag, index) => (
                      <Chip
                        key={index}
                        label={`${tag.text} (${tag.type})`}
                        size="small"
                        sx={{margin: 0.5}}
                        color={tag.isGroup ? 'default' : 'primary'}
                        data-testid={`production-guard-view-chip-${tag.text}`}
                      />
                    ))}
                  {productionGuardData.tagList.length > 10 && (
                    <Typography
                      className="typo-text body3_size"
                      sx={{marginTop: 1}}
                    >
                      ... and {productionGuardData.tagList.length - 10} more
                    </Typography>
                  )}
                </Box>
              </Box>
            )}

          {/* Full JSON Response */}
          <Box sx={{marginTop: 3}}>
            <Typography className="typo-text label2_size" gutterBottom>
              Full JSON Response
            </Typography>
            <Box
              component="pre"
              sx={{
                backgroundColor: '#fff',
                padding: 2,
                borderRadius: 1,
                maxHeight: 480,
                overflow: 'auto',
                fontSize: '12px',
                border: '1px solid #ddd',
              }}
            >
              {JSON.stringify(productionGuardData, null, 2)}
            </Box>
          </Box>
        </Box>
      )}
      {/* Initial State or No Data */}
      {!isLoading && !productionGuardData && !error && (
        <Box
          sx={{
            padding: 3,
            backgroundColor: '#fff3e0',
            borderRadius: 1,
            border: '1px solid #ff9800',
          }}
        >
          <Typography className="typo-text body2_size">
            {!enabled
              ? '👆 Click "Test API" button to make the API request'
              : '⏳ Preparing request...'}
          </Typography>
        </Box>
      )}
      {/* Debug Info (helpful for troubleshooting) */}
      {enabled && !isLoading && !productionGuardData && !error && (
        <Box
          sx={{
            padding: 2,
            backgroundColor: '#fff9e6',
            borderRadius: 1,
            border: '1px solid #ffc107',
            marginBottom: 2,
          }}
        >
          <Typography className="typo-text label2_size">
            🔍 Debug Info
          </Typography>
          <Typography className="typo-text body3_size">
            Enabled: {enabled ? 'Yes' : 'No'} | Loading:{' '}
            {isLoading ? 'Yes' : 'No'} | Has Data:{' '}
            {productionGuardData ? 'Yes' : 'No'} | Has Error:{' '}
            {error ? 'Yes' : 'No'}
          </Typography>
          <Typography className="typo-text body3_size" sx={{marginTop: 1}}>
            If you see this, the request may not have triggered. Try clicking
            Refetch.
          </Typography>
        </Box>
      )}
      {/* Network Details */}
      <Box sx={{marginTop: 4, padding: 2, backgroundColor: '#fafafa'}}>
        <Typography variant="h6" className="typo-text subhead3_size">
          📚 API Information
        </Typography>
        <Typography className="typo-text body3_size" sx={{marginTop: 1}}>
          <strong>Endpoint:</strong> GET{' '}
          /productions/api/v1/production/view/productionguardview
        </Typography>
        <Typography className="typo-text body3_size">
          <strong>Parameters:</strong> SecurityGroupId, productionId
        </Typography>
      </Box>
    </Box>
  );
};

export default ProductionGuardViewApiTest;
